import React from 'react';
import { strings } from '../resources/strings';
import { orderTags } from '../resources/orderResources';
import { StatePrescriptionIdentification } from '../enums/StatePrescriptionIdentification';
import { PrescriptionOption } from '../enums/PrescriptionOption';
import { ProfileType } from '../enums/ProfileType';
import { StateOrderIdentification } from '../enums/StateOrderIdentification';
import { AssemblyType } from '../enums/AssemblyType';
import { AssemblyDisapprovalType } from '../enums/AssemblyDisapprovalType';
import { Order } from '../models/Order';
import { OrderForListing } from '../models/OrderForListing';
import { useAuth } from '../contexts/AuthContext';
import ServicesContext from '../contexts/ServicesContext';
import { ProfileHelper } from '../helpers/ProfileHelper';
import { GenerateTSOReportInput } from '../services/Reports/GenerateTSOReport/GenerateTSOReportInput';
import { SaveStateLogInput } from '../services/StatesOrders/SaveStateLog/SaveStateLogInput';
import { SaveStateLogOutput } from '../services/StatesOrders/SaveStateLog/SaveStateLogOutput';
import { SaveStatePrescriptionLogInput } from '../services/StatesPrescriptions/SaveStatePrescriptionLog/SaveStatePrescriptionLogInput';
import { SaveStatePrescriptionLogOutput } from '../services/StatesPrescriptions/SaveStatePrescriptionLog/SaveStatePrescriptionLogOutput';
import { RegisterAssemblyDisapprovalByOrderInput } from '../services/Orders/RegisterAssemblyDisapprovalByOrder/RegisterAssemblyDisapprovalByOrderInput';

export function useOrderUtils() {

  const { session } = useAuth();

  const {
    generateTSOReportService,
    saveStateLogService,
    toastService,
    alertService,
    saveStatePrescriptionLogService,
    statesService,
    registerAssemblyDisapprovalByOrderService
  } = React.useContext(ServicesContext);

  function isCrossChannelFulfilledByEcommerceAndWithPhysicalStoreOrigin(
    isCrossChannelSale?: boolean,
    locationId?: number,
    originLocationId?: number
  ) {
    return isCrossChannelSale &&
      locationId === 1 &&
      originLocationId &&
      originLocationId > 1;
  }

  function orderIsCrossChannelFulfilledByEcommerceAndWithPhysicalStoreOrigin(order: Order) {
    return isCrossChannelFulfilledByEcommerceAndWithPhysicalStoreOrigin(
      order.isCrossChannelSale,
      order.locationId,
      order.originLocationId
    );
  }

  function orderIsCrossChannelFulfilledByEcommerceAndWithPhysicalStoreOriginAndCanBeSelected(order: Order) {
    const isCrossChannelSaleFulfilledByEcommerceAndWithPhysicalStoreOrigin =
      orderIsCrossChannelFulfilledByEcommerceAndWithPhysicalStoreOrigin(order);

    return session?.user?.profileType == ProfileType.Vendor && isCrossChannelSaleFulfilledByEcommerceAndWithPhysicalStoreOrigin
      ? false
      : true;
  }

  function printTSO(orderId: number, version?: number) {
    const input = new GenerateTSOReportInput();
    input.orderIds = [orderId];
    generateTSOReportService.handle(input, version);
  }

  function changeState(orderId: number, newStateId: any) {
    const promise: Promise<SaveStateLogOutput | null | undefined> = new Promise(async (resolve, reject) => {
      var input = new SaveStateLogInput();
      input.orderId = orderId;
      input.newStateId = newStateId;
      input.userId = session?.user?.id || 0;
      saveStateLogService.handle(input)
        .then(result => resolve(result?.value))
        .catch(error => {
          if (error.response) {
            toastService.warning(strings.OpsSomethingWrongSendTheData);
            return;
          }
          alertService.warning(strings.TheServerIsNotRespondingRefreshListAndTryAgain);
        });
    });
    return promise;
  }

  function getStatus(order?: Order | null) {
    if (order?.shopifyFormatedClosedAt) return strings.Closed;
    if (order?.shopifyFormatedCancelledAt) return strings.Canceled;
    return strings.Opened;
  }

  function saveStatePrescriptionForManufacturingProblem(orderId: number) {
    return saveNewStatePrescription(StatePrescriptionIdentification.SolicitarLente, orderId);
  }

  function saveStatePrescriptionForLensProblem(orderId: number) {
    return saveNewStatePrescription(StatePrescriptionIdentification.SolicitarLente, orderId);
  }

  function saveNewStatePrescription(statePrescriptionIdentification: StatePrescriptionIdentification, orderId: number) {
    const promise: Promise<SaveStatePrescriptionLogOutput | null | undefined> = new Promise(async (resolve, reject) => {
      const input = new SaveStatePrescriptionLogInput();
      input.newStatePrescriptionId = statePrescriptionIdentification;
      input.orderId = orderId;
      input.userId = session?.user?.id;
      saveStatePrescriptionLogService.handle(input)
        .then(result => resolve(result?.value))
        .catch(error => {
          if (error.response) {
            toastService.warning(strings.OpsSomethingWrongSendTheData);
            return;
          }
          alertService.warning(strings.TheServerIsNotRespondingRefreshListAndTryAgain);
        });
    });
    return promise;
  }

  function saveNewStateOrder(stateOrderIdentification: StateOrderIdentification, orderId: number) {
    return changeState(orderId, stateOrderIdentification);
  }

  function getButtonPrescriptionOptions(
    currentStatePrescriptionId?: number,
    orderIsPhysicalStore?: boolean,
    shopifyCustomerId?: number,
    isCrossChannelSale?: boolean,
    locationId?: number,
    originLocationId?: number
  ) {
    const optionsByPrescription = [];

    const userProfile = session?.user?.profileType;
    const firstLocationInfo = session?.locations && session?.locations?.length > 0 ? session?.locations[0] : null;

    const makeInternalAssembly = !firstLocationInfo || firstLocationInfo.assemblyType === null || firstLocationInfo.assemblyType === undefined ||
      firstLocationInfo?.assemblyType === AssemblyType.All || firstLocationInfo?.assemblyType === AssemblyType.Internal ||
      firstLocationInfo?.assemblyType === AssemblyType.InternalWithoutAssembler;
    const makeExternalAssembly = !firstLocationInfo || firstLocationInfo.assemblyType === null || firstLocationInfo.assemblyType === undefined ||
      firstLocationInfo?.assemblyType === AssemblyType.All || firstLocationInfo?.assemblyType === AssemblyType.External;

    if (!userProfile || ProfileHelper.profilesThatCanManageNotifications.some(p => p === userProfile)) {
      optionsByPrescription.push({
        key: PrescriptionOption.NotificationsV2,
        text: strings.Notifications
      });
    }

    optionsByPrescription.push({
      key: orderIsPhysicalStore || userProfile == ProfileType.Vendor || (isCrossChannelSale && !shopifyCustomerId)
        ? PrescriptionOption.RecipesForPhysicalStore
        : PrescriptionOption.RecipesV2,
      text: `${strings.Recipes}`
    });

    if (userProfile === null || userProfile === undefined || ProfileHelper.profilesThatCanEvolvePriscriptionState.some(p => p === userProfile)) {
      if (currentStatePrescriptionId === StatePrescriptionIdentification.SolicitarLente &&
         ((!isCrossChannelSale) || (isCrossChannelSale && (userProfile === ProfileType.Ecommerce || userProfile === ProfileType.Admin)))
      ) {
        optionsByPrescription.push({
          key: PrescriptionOption.RequestLens,
          text: strings.RequestLens
        });
      }

      if (
        (!isCrossChannelSale) ||
        (isCrossChannelSale && (userProfile === ProfileType.Ecommerce || userProfile === ProfileType.Admin))
      ) {
        if (currentStatePrescriptionId === StatePrescriptionIdentification.AguardandoLente) {
          if (makeInternalAssembly) {
            optionsByPrescription.push(...[
              {
                key: PrescriptionOption.RequestInternalManufacturing,
                text: strings.RequestInternalManufacturing
              }
            ]);
          }
          if (makeExternalAssembly) {
            optionsByPrescription.push(...[
              {
                key: PrescriptionOption.RequestExternalManufacturing,
                text: strings.RequestExternalManufacturing
              }
            ]);
          }
        }

        if (currentStatePrescriptionId === StatePrescriptionIdentification.AguardandoMontagemInterna) {
          optionsByPrescription.push(...[
            {
              key: PrescriptionOption.StartChecking,
              text: strings.StartCheking
            }
          ]);
          if (makeExternalAssembly) {
            optionsByPrescription.push(...[
              {
                key: PrescriptionOption.RequestExternalManufacturing,
                text: strings.RequestExternalManufacturing
              }
            ]);
          }
        }

        if (currentStatePrescriptionId === StatePrescriptionIdentification.AguardandoMontagemExterna) {
          optionsByPrescription.push(...[
            {
              key: PrescriptionOption.StartChecking,
              text: strings.StartCheking
            }
          ]);
          if (makeInternalAssembly) {
            optionsByPrescription.push(...[
              {
                key: PrescriptionOption.RequestInternalManufacturing,
                text: strings.RequestInternalManufacturing
              }
            ]);
          }
        }

        if (currentStatePrescriptionId === StatePrescriptionIdentification.AguardandoConferencia) {
          optionsByPrescription.push({
            key: PrescriptionOption.Approve,
            text: strings.ApproveConference
          });
        }

        if (currentStatePrescriptionId === StatePrescriptionIdentification.AguardandoConferencia || currentStatePrescriptionId === StatePrescriptionIdentification.Ok) {
          optionsByPrescription.push({
            key: PrescriptionOption.Disapprove,
            text: strings.DisapproveConference
          });
        }
      }
    }

    return optionsByPrescription;
  }

  const getPreviousDropdownItems = (currentStateId?: number, userProfile?: ProfileType) =>
    statesService.translateListByUserProfile(statesService.filterByUserProfile(statesService.getPrevious(currentStateId), userProfile), userProfile).map(state => {
      const item = {
        key: state?.id,
        text: state?.name
      };
      return item;
    });

  const getNextDropdownItems = (currentStateId?: number, userProfile?: ProfileType) =>
    statesService.translateListByUserProfile(statesService.filterByUserProfile(statesService.getNext(currentStateId), userProfile), userProfile).map(state => {
      const item = {
        key: state?.id,
        text: state?.name
      };
      return item;
    });

  const getDeliveryNextDropdownItems = (currentStateId?: number) =>
    statesService.getDeliveryNext(currentStateId).map(state => {
      const item = {
        key: state?.id,
        text: state?.name
      };
      return item;
    });

  const registerAssemblyDisapproval = (orderId: number, problemType: AssemblyDisapprovalType, problemDetails: string, onSuccess: () => void) => {
    if (!session?.user?.id) return;

    const input = new RegisterAssemblyDisapprovalByOrderInput();
    input.orderId = orderId;
    input.createdById = session?.user?.id;
    input.problemType = problemType;
    input.problemDetails = problemDetails;
    registerAssemblyDisapprovalByOrderService.handle(input)
      .then(result => {
        if (result?.value?.message) {
          alertService.warning(result?.value?.message);
          return;
        }
        onSuccess();
      })
      .catch(error => {
        if (error.response) {
          toastService.warning(strings.OpsSomethingWrongSendTheData);
          return;
        }
        alertService.warning(strings.TheServerIsNotRespondingRefreshListAndTryAgain);
      });
  };

  const stopChangeIfOrderForListingHasExternallyChanged = (orderForListing: OrderForListing) => {
    if (orderForListing._externallyChanged) {
      alertService.warning('Pedidos com alteração externa não podem ser modificados até que sejam retirados desta condição.');
      return true;
    }
    return false;
  };

  const stopChangeIfOrderHasExternalChangeTag = (order: Order) => {
    return stopChangeIfOrderTagsIncludesExternalChange(order?.tags);
  };

  const stopChangeIfOrderTagsIncludesExternalChange = (tags?: string) => {
    if (tags?.includes(orderTags.externally_changed.value)) {
      alertService.warning('Pedidos com alteração externa não podem ser modificados até que sejam retirados desta condição.');
      return true;
    }
    return false;
  };

  return {
    printTSO,
    changeState,
    getStatus,
    saveStatePrescriptionForManufacturingProblem,
    saveStatePrescriptionForLensProblem,
    saveNewStatePrescription,
    saveNewStateOrder,
    getButtonPrescriptionOptions,
    getPreviousDropdownItems,
    getNextDropdownItems,
    getDeliveryNextDropdownItems,
    registerAssemblyDisapproval,
    stopChangeIfOrderForListingHasExternallyChanged,
    stopChangeIfOrderHasExternalChangeTag,
    stopChangeIfOrderTagsIncludesExternalChange,
    isCrossChannelFulfilledByEcommerceAndWithPhysicalStoreOrigin,
    orderIsCrossChannelFulfilledByEcommerceAndWithPhysicalStoreOriginAndCanBeSelected,
    orderIsCrossChannelFulfilledByEcommerceAndWithPhysicalStoreOrigin
  };
}